<script setup>
import { Inertia } from "@inertiajs/inertia";
import { computed, onMounted } from 'vue';
import TimeSlotAppointment from "./TimeSlotAppointment.vue";

const props = defineProps({
    timeslots: Array,
    filterView: String, // filterView: tag_behandler, tag_raum, woche_behandler
    practitioner: Object, // only for filterView: tag_behandler
    room: Object, // only for filterView: tag_raum
    hideNames: Boolean,
});

// compute if all timeslots have the same special date
const special_date_all = computed(() => {
    let special_date = null;
    props.timeslots.every(timeslot => {
        if (timeslot.special_date) {
            if (special_date == null) {
                special_date = timeslot.special_date;
            } else if (special_date.name !== timeslot.special_date.name) {
                special_date = null;
                return false;
            }
            return true;
        } else {
            special_date = null;
            return false;
        }
    });
    return special_date;
});

const zeroPad = (num, places) => String(num).padStart(places, '0')
const practitionerAvailable = computed(() => {
    let available = true;

    if (props.filterView == 'tag_behandler') {
        let workingHours = props.practitioner.working_hours;
        let datestart = new Date(props.timeslots[0].start);
        let timestart = zeroPad(datestart.getHours(), 2) + ":" + zeroPad(datestart.getMinutes(), 2) + ":00";
        let dateend = new Date(props.timeslots[0].end);
        let timeend = zeroPad(dateend.getHours(), 2) + ":" + zeroPad(dateend.getMinutes(), 2) + ":00";
        
        let day = datestart.getDay();
        if (day == 0) day = 7; // ISO: sunday = 7 (not 0)
        let workingHour = workingHours.find(workingHour => workingHour.weekday == day);

        if (workingHour) {
          if (workingHour.open) {
            if (workingHour.start <= timestart && workingHour.end >= timeend) {
                available = true;
            } else if(workingHour.hasBreak && workingHour.start_2 <= timestart && workingHour.end_2 >= timeend) {
                available = true;
            } else {
                available = false;
            }
          } else {
            available = false;
          }
        } else {
            available = false;
        }
    }

    return available;
});

/* onMounted(() => {
    if (props.timeslots[0].start == "2023-07-19 10:00:00") {
        console.log('working_hours', props.practitioner.working_hours);
        console.log('timeslots', props.timeslots[0]);
    }
}); */

function entryClick(timeslot, type) {
    if (type == 'normal' && timeslot.appointment) { // click on normal appointment
        Inertia.get(`/termin-bearbeiten/` + timeslot.appointment.id);
    } else if (type == 'parallel' && timeslot.appointment_parallel) { // click on parallel appointment
        Inertia.get(`/termin-bearbeiten/` + timeslot.appointment_parallel.id);
    } else if (props.filterView != 'woche_behandler') { // click on empty timeslot
        let startDateTime = new Date(timeslot.start);
        let startDate = startDateTime.getFullYear() + "-" + (startDateTime.getMonth() + 1) + "-" + startDateTime.getDate();
        let startTime = ("0" + startDateTime.getHours()).slice(-2) + "%3A" + ("0" + startDateTime.getMinutes()).slice(-2); // %3A = :
        let redirect = "/termin-erstellen?date=" + startDate + "&time=" + startTime;
        if (type == 'parallel') {
            redirect += "&parallel=true";
        }
        if (props.filterView == 'tag_behandler' && props.practitioner) {
            redirect += "&behandler=" + props.practitioner.id;
        }
        if (props.filterView == 'tag_raum' && props.room) {
            redirect += "&raum=" + props.room.id;
        }
        Inertia.get(redirect);
    }
}
</script>

<template>
<div
    class="oteo-entry flex justify-between w-full"
    :class="{
        'vacation-office' : props.timeslots[0].vacation_office,
        'vacation-office-start' : props.timeslots[0].vacation_office && props.timeslots[0].vacation_office.start == props.timeslots[0].start,
        'vacation-office-end' : props.timeslots[0].vacation_office && props.timeslots[0].vacation_office.end == props.timeslots[0].end,
        'not-open' : !props.timeslots[0].open,
        'not-available' : !practitionerAvailable,
    }"
>
    <!-- special date for all practitioners -->
    <div
        v-if="special_date_all && (props.filterView == 'woche_behandler')"
        class="oteo-specialdate"
        :class="{ 'oteo-specialdate-end' : special_date_all.end == props.timeslots[0].end }"
        :title="'Sondertermin ' + special_date_all.name"
    >
        <div v-if="special_date_all.start === props.timeslots[0].start" class="flex justify-center items-center h-full">
            <div class="text-xs">{{ special_date_all.name }}</div>
        </div>
    </div>

    <template
        v-for="timeslot in props.timeslots"
        :key="timeslot.start"
    >
        <div
            v-if="timeslot.special_date && (props.filterView == 'tag_behandler')"
            class="oteo-specialdate"
            :class="{ 'oteo-specialdate-end' : timeslot.special_date.end == timeslot.end }"
            :title="'Sondertermin ' + timeslot.special_date.name"
        >
            <div v-if="timeslot.special_date.start === timeslot.start" class="flex justify-center items-center h-full">
                <div class="text-xs">{{ timeslot.special_date.name }}</div>
            </div>
        </div>

        <div
            v-if="(props.filterView == 'tag_behandler')
                || ((props.filterView == 'tag_raum') && timeslot.appointment)
                || ((props.filterView == 'woche_behandler') && timeslot.hasDailyAppointment)"
            class="oteo-entry-col"
            :class="{
                'vacation-practitioner' : (props.filterView == 'tag_behandler') && timeslot.vacation_practitioner,
                'vacation-practitioner-start' : (props.filterView == 'tag_behandler') && timeslot.vacation_practitioner && timeslot.vacation_practitioner.start == timeslot.start,
                'vacation-practitioner-end' : (props.filterView == 'tag_behandler') && timeslot.vacation_practitioner && timeslot.vacation_practitioner.end == timeslot.end,
                'emergency' : (props.filterView == 'tag_behandler') && timeslot.emergency_service,
                'emergency-start' : (props.filterView == 'tag_behandler') && timeslot.emergency_service && timeslot.emergency_service.time_start == timeslot.start,
                'emergency-end' : (props.filterView == 'tag_behandler') && timeslot.emergency_service && timeslot.emergency_service.time_end == timeslot.end,
            }"
            :style="`width: ${100 / timeslot.concurrentCount}%;`"
            @click="entryClick(timeslot, 'normal')"
        >
            <div
                v-if="timeslot.appointment"
                class="appointment"
            >
                <TimeSlotAppointment
                    :appointment="timeslot.appointment"
                    :timeslotType="timeslot.type"
                    :filterView="props.filterView"
                    :concurrentCount="timeslot.concurrentCount"
                    :hideNames="props.hideNames"
                />
            </div>
        </div>

        <div
            v-if="(props.filterView == 'tag_behandler')
                || ((props.filterView == 'tag_raum') && timeslot.appointment_parallel)
                || ((props.filterView == 'woche_behandler') && timeslot.hasDailyAppointmentParallel)"
            class="oteo-entry-col parallel"
            :class="{
                'vacation-practitioner' : (props.filterView == 'tag_behandler') && timeslot.vacation_practitioner,
                'vacation-practitioner-start' : (props.filterView == 'tag_behandler') && timeslot.vacation_practitioner && timeslot.vacation_practitioner.start == timeslot.start,
                'vacation-practitioner-end' : (props.filterView == 'tag_behandler') && timeslot.vacation_practitioner && timeslot.vacation_practitioner.end == timeslot.end,
                'emergency' : (props.filterView == 'tag_behandler') && timeslot.emergency_service,
                'emergency-start' : (props.filterView == 'tag_behandler') && timeslot.emergency_service && timeslot.emergency_service.time_start == timeslot.start,
                'emergency-end' : (props.filterView == 'tag_behandler') && timeslot.emergency_service && timeslot.emergency_service.time_end == timeslot.end,
            }"
            :style="`width: ${100 / timeslot.concurrentCount}%;`"
            @click="entryClick(timeslot, 'parallel')"
        >
            <div
                v-if="timeslot.appointment_parallel"
                class="appointment"
            >
                <TimeSlotAppointment
                    :appointment="timeslot.appointment_parallel"
                    :timeslotType="timeslot.type_parallel"
                    :filterView="props.filterView"
                    :concurrentCount="timeslot.concurrentCount"
                    :hideNames="props.hideNames"
                />
            </div>
        </div>

        <div
            v-if="(props.filterView == 'tag_raum') && !timeslot.appointment && !timeslot.appointment_parallel"
            class="oteo-entry-col"
            style="width: 100%;"
            @click="entryClick(timeslot, 'normal')"
        ></div>
    </template>
</div>
</template>

<style lang="scss">
.oteo-entry {
    height: 100%;
    padding: 0 1px;

    .oteo-specialdate {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        width: 100%;
        background-color: #ccc;
        color: #fff;
        font-weight: bold;
        z-index: 2;
        pointer-events: none;
        opacity: 0.8;

        &:not(.oteo-specialdate-end) {
            bottom: -1px;
        }
    }

    &-col {
        height: 100%;
        width: 100%;
        position: relative;

        .appointment {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 10;
        }
    }
}

.emergency {
    &:after {
        content: '';
        position: absolute;
        top: -1px;
        bottom: -1px;
        left: -2px;
        right: -1px;
        background-color: rgba(255, 0, 0, .005);
        border-left: 1px solid rgba(255, 0, 0, .7);
        border-right: 1px solid rgba(255, 0, 0, .1);
        border-top: 1px solid rgba(255, 0, 0, .1);
        border-bottom: 1px solid rgba(255, 0, 0, .1);
        -webkit-background-clip: padding-box;
        background-clip: padding-box;
        z-index: 1;
        pointer-events: none;
    }

    &.parallel {
        &:after {
            left: -1px;
            right: -2px;
            border-left: 1px solid rgba(255, 0, 0, .1);
            border-right: 1px solid rgba(255, 0, 0, .7);
        }
    }
}

.emergency-start {
    &:after {
        border-top: 1px solid rgba(255, 0, 0, .7);
    }
}
.emergency-end {
    &:after {
        border-bottom: 1px solid rgba(255, 0, 0, .7);
    }
}

.vacation-practitioner {
    background-color: #f7f7f7;
    /*&:after {
        content: '';
        position: absolute;
        top: -1px;
        bottom: -1px;
        left: -2px;
        right: -1px;
        background-color: rgba(255, 0, 0, .005);
        border-left: 1px solid rgba(255, 0, 0, .5);
        border-right: 1px solid rgba(255, 0, 0, .08);
        border-top: 1px solid rgba(255, 0, 0, .08);
        border-bottom: 1px solid rgba(255, 0, 0, .08);
        -webkit-background-clip: padding-box;
        background-clip: padding-box;
        z-index: 1;
        pointer-events: none;
    }

    &.parallel {
        &:after {
            left: -1px;
            right: -2px;
            border-left: 1px solid rgba(255, 0, 0, .08);
            border-right: 1px solid rgba(255, 0, 0, .5);
        }
    }*/
}

.vacation-office {
    background-color: #f7f7f7;
    /*&:after {
        content: '';
        position: absolute;
        top: -1px;
        bottom: -1px;
        left: -1px;
        right: -1px;
        background-color: rgba(255, 0, 0, .005);
        border-left: 1px solid rgba(255, 0, 0, .5);
        border-right: 1px solid rgba(255, 0, 0, .5);
        border-top: 1px solid rgba(255, 0, 0, .08);
        border-bottom: 1px solid rgba(255, 0, 0, .08);
        -webkit-background-clip: padding-box;
        background-clip: padding-box;
        z-index: 1;
        pointer-events: none;
    }*/
}

/*.vacation-practitioner-start,
.vacation-office-start {
    &:after {
        border-top: 1px solid rgba(255, 0, 0, .5);
    }
}
.vacation-practitioner-end,
.vacation-office-end {
    &:after {
        border-bottom: 1px solid rgba(255, 0, 0, .5);
    }
}*/

.not-open,
.not-available {
    background-color: #f7f7f7;
}

/*.oteo-full-hour {
    .vacation-practitioner,
    .vacation-office {
        &:after {
            border-top: 1px solid rgba(255, 0, 0, .7);
        }
    }
    .emergency {
        &:after {
            border-top: 1px solid rgba(255, 165, 0, .7);
        }
    }
}
.oteo-entry-wrapper.last {
    .vacation-practitioner,
    .vacation-office {
        &:after {
            border-bottom: 1px solid rgba(255, 0, 0, .7);
        }
    }
    .emergency {
        &:after {
            border-bottom: 1px solid rgba(255, 165, 0, .7);
        }
    }
}*/


#oteo-calendar:not(.woche_behandler) {
    .oteo-entry-col:hover {
        background-color: #efefef;
        cursor: pointer;
    }
}

#oteo-calendar.tag_behandler {
    .oteo-entry-col.parallel {
        border-left: 1px solid #efefef;
    }
}
</style>
